import { ServiceLevel } from '@provision/service-level-utils';
import { findBusinessDayEnd, findNthBusinessDayEnd, hasRemainingBusinessHoursToday, isInCustomSchedule, isWithinBusinessHours, nextBusinessDay, nextWeekdayAtTime, } from './business-hours-utils';
import { buildDateAtTime, buildDateFromLocalParts, getLocalDateParts, incrementLocalDate, isWeekDay, toTimeParts, } from './date-timezone-utils';
const MIDDAY_TIME = '12:00';
const END_OF_DAY_TIME = '17:00';
function nextCalendarDayAtTime(from, timezone, preferredTime) {
    const time = toTimeParts(preferredTime);
    if (!time) {
        return null;
    }
    const local = getLocalDateParts(from, timezone);
    const nextDay = incrementLocalDate({
        year: local.year,
        month: local.month,
        day: local.day,
    });
    return buildDateFromLocalParts({
        ...nextDay,
        hour: time.hour,
        minute: time.minute,
    }, timezone);
}
export const SERVICE_LEVEL_PRIORITY_ORDER = [
    ServiceLevel.NonMember,
    ServiceLevel.SecurityOnly,
    ServiceLevel.Essentials,
    ServiceLevel.EssentialsPlus,
    ServiceLevel.Priority,
    ServiceLevel.Proactive,
    ServiceLevel.Signature,
];
const SERVICE_LEVEL_SET = new Set(SERVICE_LEVEL_PRIORITY_ORDER);
function normalizeRuleServiceLevels(serviceLevels) {
    if (serviceLevels === 'all') {
        return 'all';
    }
    const normalized = [];
    const seen = new Set();
    for (const serviceLevel of serviceLevels) {
        if (!SERVICE_LEVEL_SET.has(serviceLevel) || seen.has(serviceLevel)) {
            continue;
        }
        normalized.push(serviceLevel);
        seen.add(serviceLevel);
    }
    return normalized;
}
/**
 * Build an effective context for condition matching.
 * Uses escalationCutoffHours for condition checks when available,
 * falling back to businessHours if not set.
 */
function buildConditionContext(ctx) {
    return {
        ...ctx,
        businessHours: ctx.escalationCutoffHours ?? ctx.businessHours,
    };
}
const TIME_CONDITION_MATCHERS = {
    always: () => true,
    business_hours: (_, ctx) => {
        const effectiveCtx = buildConditionContext(ctx);
        return isWithinBusinessHours(effectiveCtx) || hasRemainingBusinessHoursToday(effectiveCtx);
    },
    outside_business_hours: (_, ctx) => {
        const effectiveCtx = buildConditionContext(ctx);
        return (!isWithinBusinessHours(effectiveCtx) && !hasRemainingBusinessHoursToday(effectiveCtx));
    },
    custom: (rule, context) => isInCustomSchedule(rule.timeCondition.customSchedule, context),
};
function matchesTimeCondition(rule, context) {
    const matcher = TIME_CONDITION_MATCHERS[rule.timeCondition.type];
    return matcher ? matcher(rule, context) : false;
}
const DEADLINE_CALCULATORS = {
    fixed_duration: (rule, context) => {
        const { durationMinutes = 0 } = rule.response;
        if (durationMinutes <= 0) {
            return null;
        }
        return new Date(context.currentTime.getTime() + durationMinutes * 60 * 1000);
    },
    within_business_days: (rule, context) => findNthBusinessDayEnd(context.currentTime, context, rule.response.businessDays ?? 0),
    by_fixed_time: (rule, context) => {
        const time = rule.response.time ?? END_OF_DAY_TIME;
        const { weekDay } = rule.response;
        if (isWeekDay(weekDay)) {
            return nextWeekdayAtTime(context.currentTime, { ...context, weekDay }, time);
        }
        const todayTime = buildDateAtTime(context.currentTime, time, context.timezone);
        if (!todayTime) {
            return null;
        }
        if (todayTime > context.currentTime) {
            return todayTime;
        }
        return nextBusinessDay(context.currentTime, context, time);
    },
    by_end_of_day: (_, context) => findBusinessDayEnd(context.currentTime, context),
    by_next_business_day: (rule, context) => nextBusinessDay(context.currentTime, context, rule.response.time ?? MIDDAY_TIME),
    by_tomorrow: (rule, context) => nextCalendarDayAtTime(context.currentTime, context.timezone, rule.response.time ?? MIDDAY_TIME),
    best_effort: () => null,
};
function calculateDeadline(rule, context) {
    const calc = DEADLINE_CALCULATORS[rule.response.type];
    return calc ? calc(rule, context) : null;
}
function isValidEscalationRule(rule) {
    if (!rule || typeof rule !== 'object') {
        return false;
    }
    const c = rule;
    return (typeof c.id === 'string' &&
        (c.serviceLevels === 'all' ||
            (Array.isArray(c.serviceLevels) &&
                c.serviceLevels.every((value) => typeof value === 'string'))) &&
        typeof c.timeCondition === 'object' &&
        c.timeCondition !== null &&
        typeof c.response === 'object' &&
        c.response !== null);
}
function extractRulesArray(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'object' && value !== null) {
        const maybe = value.rules;
        if (Array.isArray(maybe)) {
            return maybe;
        }
    }
    return [];
}
export function parseEscalationRules(value) {
    if (!value) {
        return [];
    }
    let raw = [];
    if (typeof value === 'string') {
        try {
            raw = extractRulesArray(JSON.parse(value));
        }
        catch {
            console.error('Failed to parse JSON', value);
        }
    }
    else {
        raw = extractRulesArray(value);
    }
    const valid = raw.filter(isValidEscalationRule);
    return valid.map((rule, index) => ({
        ...rule,
        serviceLevels: normalizeRuleServiceLevels(rule.serviceLevels),
        order: Number.isFinite(rule.order) ? rule.order : index,
    }));
}
export function matchEscalationRule(rules, serviceLevel, context) {
    const effectiveServiceLevel = context.isPreLits ? ServiceLevel.Priority : serviceLevel;
    const sorted = [...rules].sort((a, b) => a.order - b.order);
    const matched = sorted.find((rule) => (rule.serviceLevels === 'all' || rule.serviceLevels.includes(effectiveServiceLevel)) &&
        matchesTimeCondition(rule, context));
    if (!matched) {
        return null;
    }
    return {
        rule: matched,
        deadline: calculateDeadline(matched, context),
    };
}
export function formatResponseExpectation(match, serviceLevel, timezone) {
    if (!match) {
        if (serviceLevel) {
            return `Not available for ${serviceLevel} clients`;
        }
        return 'Not available';
    }
    if (match.rule.response.type === 'best_effort' || !match.deadline) {
        return "We'll get back to you as soon as we can (no hard time commitment)";
    }
    return `Response expected by ${formatTimeShort(match.deadline, timezone)}`;
}
const WEEKDAY_LABELS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
const RULE_RESPONSE_LABELS = {
    fixed_duration: (r) => `within ${r.response.durationMinutes ?? 0} minutes`,
    within_business_days: (r) => formatWithinBusinessDays(r.response.businessDays ?? 0),
    by_end_of_day: () => 'by end of day',
    by_fixed_time: (r) => {
        const time = r.response.time ?? '5:00 PM';
        const { weekDay } = r.response;
        if (isWeekDay(weekDay)) {
            return `by ${time} on ${WEEKDAY_LABELS[weekDay]}`;
        }
        return `by ${time}`;
    },
    by_next_business_day: (r) => `by ${r.response.time ?? '12:00'} next business day`,
    by_tomorrow: (r) => `by ${r.response.time ?? '12:00'} tomorrow`,
    best_effort: () => 'best effort',
};
export function formatWithinBusinessDays(businessDays) {
    return `within ${businessDays} business day${businessDays === 1 ? '' : 's'}`;
}
export function formatRuleResponseLabel(rule) {
    const formatter = RULE_RESPONSE_LABELS[rule.response.type];
    return formatter ? formatter(rule) : 'best effort';
}
export function formatTimeShort(time, timezone) {
    if (!time) {
        return 'Not available';
    }
    return time.toLocaleString([], {
        hour: 'numeric',
        minute: '2-digit',
        weekday: 'short',
        ...(timezone ? { timeZone: timezone } : {}),
    });
}
export function calculateUpgradePath(rules, currentServiceLevel, context) {
    const currentOrder = SERVICE_LEVEL_PRIORITY_ORDER.indexOf(currentServiceLevel);
    if (currentOrder < 0) {
        return [];
    }
    return SERVICE_LEVEL_PRIORITY_ORDER.slice(currentOrder + 1).map((serviceLevel) => {
        const match = matchEscalationRule(rules, serviceLevel, context);
        return {
            serviceLevel,
            expectation: formatResponseExpectation(match, serviceLevel, context.timezone),
            deadline: match?.deadline ?? null,
            unavailable: !match,
        };
    });
}
const EXPECTATION_CHANGE_LOOKAHEAD_DAYS = 7;
const MIDNIGHT = '00:00';
/**
 * Two matches set the same expectation when a specialist would quote the same thing: the same
 * deadline, or for a fixed duration (whose deadline moves with the clock) the same duration.
 * Rule identity alone is not enough: an overnight "by 10:00 tomorrow" handing over at midnight
 * to an early-morning "by 10:00" keeps the same deadline.
 */
function isSameExpectation(current, next) {
    if (!current || !next) {
        return current === next;
    }
    const currentResponse = current.rule.response;
    const nextResponse = next.rule.response;
    if (currentResponse.type === 'fixed_duration' || nextResponse.type === 'fixed_duration') {
        return (currentResponse.type === nextResponse.type &&
            currentResponse.durationMinutes === nextResponse.durationMinutes);
    }
    return current.deadline?.getTime() === next.deadline?.getTime();
}
/**
 * Every instant in the lookahead where a match or deadline can flip: window edges of the hours
 * and schedules the rules read, response times, local midnight and holiday edges. Between two of
 * these the matched rule and its deadline stay put.
 */
function listExpectationBoundaries(rules, context) {
    const { currentTime, timezone } = context;
    const windows = [
        ...(context.businessHours ?? []),
        ...(context.escalationCutoffHours ?? []),
        ...rules.flatMap((rule) => rule.timeCondition.customSchedule ?? []),
    ];
    const times = new Set([
        MIDNIGHT,
        ...windows.flatMap((w) => [w.timeFrom, w.timeTo]),
        ...rules.flatMap((rule) => rule.response.time ?? []),
    ]);
    const boundaries = [];
    const local = getLocalDateParts(currentTime, timezone);
    let day = { year: local.year, month: local.month, day: local.day };
    for (let i = 0; i <= EXPECTATION_CHANGE_LOOKAHEAD_DAYS; i += 1) {
        times.forEach((time) => {
            const instant = buildDateAtLocalDay(day, time, timezone);
            if (instant)
                boundaries.push(instant);
        });
        day = incrementLocalDate(day);
    }
    (context.holidays ?? []).forEach((holiday) => {
        boundaries.push(new Date(holiday.holidayStart), new Date(holiday.holidayEnd));
    });
    return boundaries
        .filter((instant) => instant.getTime() > currentTime.getTime())
        .sort((a, b) => a.getTime() - b.getTime());
}
function buildDateAtLocalDay(day, time, timezone) {
    const parts = toTimeParts(time);
    return parts ? buildDateFromLocalParts({ ...day, ...parts }, timezone) : null;
}
/**
 * When the response expectation for this service level next changes within the coming week, and
 * what it changes to. Null when it holds for the whole lookahead.
 */
export function findNextExpectationChange(rules, serviceLevel, context) {
    const current = matchEscalationRule(rules, serviceLevel, context);
    for (const at of listExpectationBoundaries(rules, context)) {
        const match = matchEscalationRule(rules, serviceLevel, { ...context, currentTime: at });
        if (!isSameExpectation(current, match)) {
            return { at, match };
        }
    }
    return null;
}
